import animateScrollTo from 'animated-scroll-to';
import { useEffect, useCallback, useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@components/Common/Button';
import Hint from '@components/Common/Hint';
import SkeepersIcon from '@components/Common/SkeepersIcon';
import Spinner from '@components/Common/Spinner';
import PlayerControls from '@components/Project/PlayerControls';
import SilenceBlock from '@components/Studio/SilenceBlock';
import TimelineBase from '@components/Studio/TimelineBase';

import { Waveform } from '../Common/Waveform';

import style from './style.module.css';

const SilenceSmartCut = ({ media, onSmartCut = () => { }, start = 0, duration = null }) => {
  const { t } = useTranslation();
  const [video] = useState(media);
  const [silenceBlocks, setSilenceBlocks] = useState([]);
  const [sensitivity, setSensitivity] = useState(25);
  const [containerRef, setContainerRef] = useState(null);
  const [containerWidth, setContainerWidth] = useState();
  const [scrollContainerRef, setScrollContainerRef] = useState(null);
  const [maxDuration, setMaxDuration] = useState(null);
  const [realDuration, setRealDuration] = useState(null);
  const [currentTime, setCurrentTime] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [canvasRef, setCanvasRef] = useState();
  const [zoom, setZoom] = useState(1);
  const [offsetX, setOffsetX] = useState(0);
  const playerRef = useRef();
  const waveform = useMemo(() => new Waveform({
    media: video,
    sensitivity: 0.1,
    callback: () => setIsLoading(false),
  }), [video]);

  let containerMargin = 0;

  const onPlay = () => {
    setIsPlaying(true);
  };

  const onPause = () => {
    setIsPlaying(false);
  };

  const marginStart = useMemo(() => (start * containerWidth) / realDuration, [start, containerWidth, realDuration]);
  const marginEnd = useMemo(() => (
    (maxDuration - duration - start) * containerWidth) / realDuration,
    [duration, start, containerWidth, realDuration, maxDuration]
  );

  const calcProgressPosition = useCallback(() => {
    const width = (containerRef?.getBoundingClientRect().width - containerMargin * 2) * zoom;
    return containerMargin + (currentTime / realDuration) * width - offsetX - marginStart * zoom;
  }, [containerMargin, containerRef, currentTime, realDuration, offsetX, zoom, marginStart]);

  const centerTimeCursor = useCallback(() => {
    const progressPosition = ((playerRef.current.currentTime * 1000) / realDuration) * (containerWidth * zoom) - marginStart * zoom;

    animateScrollTo([progressPosition - containerWidth / 2, null], {
      elementToScroll: scrollContainerRef,
      horizontal: true,
      speed: 0,
      minDuration: 0,
      maxDuration: 0,
      cancelOnUserAction: false,
    });
  }, [scrollContainerRef, containerWidth, realDuration, zoom, marginStart]);

  const pause = useCallback(() => {
    if (playerRef?.current && isPlaying) {
      playerRef.current.pause();
    }
  }, [isPlaying]);

  const onTimeUpdate = useCallback(({ target }) => {
    setCurrentTime(Math.round(target.currentTime * 1000));

    if (!target.paused) {
      const silenceBlock = silenceBlocks
        .filter((block) => block.enabled)
        .find((block) => target.currentTime * 1000 >= block.start && target.currentTime * 1000 <= block.start + block.duration);
      if (silenceBlock) {
        if (silenceBlock.start + silenceBlock.duration >= start + realDuration) {
          seek(silenceBlock.start);
          pause();
        } else {
          seek(silenceBlock.start + silenceBlock.duration);
        }
      }
    }
  }, [silenceBlocks, start, realDuration, pause]);

  const onCanPlay = useCallback(() => {
    setMaxDuration(playerRef.current.duration * 1000);
  }, []);

  useEffect(() => {
    if (maxDuration) {
      setRealDuration(maxDuration * 1000 - start - (maxDuration * 1000 - duration - start));
    }
  }, [start, duration, currentTime, maxDuration]);

  useEffect(() => {
    if (playerRef?.current) {
      const player = playerRef.current;

      player.addEventListener('play', onPlay);
      player.addEventListener('pause', onPause);
      player.addEventListener('timeupdate', onTimeUpdate);
      player.addEventListener('canplaythrough', onCanPlay);

      return () => {
        player.removeEventListener('play', onPlay);
        player.removeEventListener('pause', onPause);
        player.removeEventListener('timeupdate', onTimeUpdate);
        player.removeEventListener('canplaythrough', onCanPlay);
      };
    }
  }, [playerRef, onTimeUpdate, onCanPlay]);

  const callback = useCallback((data) => {
    setSilenceBlocks(
      data
        .filter((block) => (block.start + block.duration) * 100 > start && block.start * 100 < start + realDuration)
        .map((block) => {
          if (block.start * 100 < start) {
            return {
              start: start,
              duration: block.duration * 100 - (start - block.start * 100),
              enabled: true,
            };
          }

          if ((block.start + block.duration) * 100 > start + realDuration) {
            return {
              start: block.start * 100,
              duration: start + realDuration - block.start * 100,
              enabled: true,
            };
          }

          return {
            start: block.start * 100,
            duration: block.duration * 100,
            enabled: true,
          };
        })
    );
  }, [start, realDuration]);

  useEffect(() => {
    if (!isLoading && canvasRef && containerRef) {
      waveform.drawWaveform(canvasRef);
      waveform.detectSilence(sensitivity * 2 / 1000, callback);
    };
  }, [waveform, canvasRef, isLoading, containerRef, sensitivity, callback]);

  const play = () => {
    if (playerRef?.current && !isPlaying) {
      playerRef.current.play();
      const lastSilence = silenceBlocks.length && silenceBlocks[silenceBlocks.length - 1];
      if (
        currentTime > start + realDuration ||
        (lastSilence && currentTime >= lastSilence.start && lastSilence.start + lastSilence.duration === start + realDuration)
      ) {
        seek(start);
      }
    }
  };

  const seek = (timecode) => {
    setCurrentTime(timecode);
    if (playerRef?.current) {
      playerRef.current.currentTime = timecode / 1000;
    }
  };

  const onClick = useCallback((e) => {
    if (e.target === canvasRef) {
      const time = (e.nativeEvent.offsetX * realDuration) / (containerRef.getBoundingClientRect().width * zoom);
      seek(time);
    }
  }, [realDuration, canvasRef, containerRef, zoom]);

  const onContainerResize = useCallback((e) => {
    setContainerWidth(e[0].contentRect.width);
  }, []);

  useEffect(() => {
    if (containerRef) {
      const observer = new ResizeObserver(onContainerResize);
      observer.observe(containerRef);
      return () => observer.unobserve(containerRef);
    }
  }, [onContainerResize, containerRef]);

  const onCut = () => {
    const orderedSilences = silenceBlocks.filter((block) => block.enabled).sort((b1, b2) => b1.start - b2.start);
    if (orderedSilences.length) {
      const values = [
        {
          start: start,
          duration: orderedSilences[0].start - start,
        },
      ];
      for (let i = 0; i < orderedSilences.length - 1; i++) {
        values.push({
          start: orderedSilences[i].start + orderedSilences[i].duration,
          duration: orderedSilences[i + 1].start - (orderedSilences[i].start + orderedSilences[i].duration),
        });
      }
      values.push({
        start: orderedSilences[orderedSilences.length - 1].start + orderedSilences[orderedSilences.length - 1].duration,
        duration: start + realDuration - (orderedSilences[orderedSilences.length - 1].start + orderedSilences[orderedSilences.length - 1].duration),
      });

      onSmartCut(values.filter((elem) => elem.duration > 0));
    }
  };

  const onScroll = (e) => {
    setOffsetX(e.target.scrollLeft);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (isPlaying) {
        centerTimeCursor();
        if (currentTime < start) {
          seek(start);
        }
        if (currentTime > start + realDuration) {
          pause();
        }
      }
    }, 1);
    return () => clearInterval(interval);
  }, [isPlaying, currentTime, start, centerTimeCursor, pause, realDuration]);

  return (
    <div className={style.window}>
      <div className={style.tuto}>
        <p>{t('Automatically detect silences and remove them from your video. Sections in red will be removed in the timeline.')}</p>
        <p>{t('Click on highlighted sections to keep them in the video and zoom in to adjust the timing of the silences.')}</p>
      </div>
      <video className={style.video} ref={playerRef}>
        <source src={video} />
      </video>
      <div className={style.controls}>
        <PlayerControls
          duration={maxDuration}
          currentTime={currentTime}
          onPlay={() => play()}
          isPlaying={isPlaying}
          onPause={() => pause()}
          onSeek={(t) => seek(t)}
        />
        <div className={style.zoomContainer}>
          <SkeepersIcon type="ZOOM_MINUS" onClick={() => (setZoom(zoom - 0.1 < 1 ? 1 : zoom - 0.1))} />
          <input type="range" min={1} max={10} step={0.1} value={zoom} onChange={(e) => setZoom(parseFloat(e.target.value))} />
          <SkeepersIcon type="ZOOM_PLUS" onClick={() => (setZoom(zoom + 0.1 > 10 ? 10 : zoom + 0.1))} />
        </div>
      </div>
      <div className={style.timeline}>
        {!isLoading && (
          <TimelineBase
            scaleX={((containerWidth + marginStart + marginEnd) / (maxDuration / 1000)) * zoom}
            scaleY={1}
            duration={maxDuration / 1000}
            onSeek={({ time }) => seek(time * 1000 + start)}
            offsetX={offsetX}
          />
        )}
        <div ref={setScrollContainerRef} className={`${style.scrollContainer} ${isPlaying ? style.noscroll : ''}`} onScroll={onScroll}>
          {!isLoading && <div className={style.progressBar} style={{ left: `${calcProgressPosition()}px` }} />}
          <div ref={setContainerRef} className={style.container} onClick={onClick}>
            {isLoading && (
              <div className={style.spinnerContainer}>
                <Spinner type="SECONDARY" />
              </div>
            )}

            <div
              className={style.hideOverflowContainer}
              style={{
                width: `${containerWidth * zoom}px`,
              }}
            >
              <canvas
                ref={setCanvasRef}
                className={style.waveform}
                style={{
                  width: `${(containerWidth + marginStart + marginEnd) * zoom}px`,
                  left: `-${marginStart * zoom}px`,
                }}
              />
            </div>
            {!isLoading &&
              silenceBlocks.map((block, idx) => (
                <SilenceBlock
                  key={idx}
                  containerWidth={containerWidth * zoom}
                  videoDuration={realDuration}
                  {...block}
                  onEdit={(newBlock) => setSilenceBlocks((lastBlocks) => lastBlocks.map((block, i) => (i === idx ? { ...block, ...newBlock } : block)))}
                  otherBlocks={silenceBlocks.filter((block, i) => i !== idx)}
                  marginStart={marginStart * zoom}
                />
              ))}
          </div>
        </div>
      </div>
      <div className={style.actions}>
        <div className={style.sensitivityContainer}>
          <div className={style.sensitivityRangeContainer}>
            <div className={style.sensitivityIconsContainer} onClick={() => {
              setSensitivity(sensitivity - 1 <= 0 ? 0 : sensitivity - 1);
            }}>
              <SkeepersIcon type="MINUS" className={style.sensitivityIcons} />
            </div>
            <span>{sensitivity}</span>
            <div className={style.sensitivityIconsContainer} onClick={() => {
              setSensitivity(sensitivity + 1 >= 100 ? 100 : sensitivity + 1);
            }}>
              <SkeepersIcon type="PLUS" className={style.sensitivityIcons} />
            </div>
          </div>
          <Button className={style.sensitivityBtn} color="#e0e0e0" label={t('Reset')} action={() => setSensitivity(25)} />
        </div>
        <Button label={t('Cut')} action={onCut} />
      </div>
      <div className={style.sliderContainer}>
          <input type="range" min="0" max="100" step="1" value={sensitivity} onChange={(e) => setSensitivity(parseInt(e.target.value))} />
          <Hint message={t('Use the slider or buttons to adjust the sensitivity of the silences.')} />
        </div>
    </div>
  );
};

export default SilenceSmartCut;
